import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AdminRoute } from "@/components/AdminRoute";
import { RoleGuard } from "@/components/RoleGuard";
import { ModuleGuard } from "@/components/ModuleGuard";
import { AuthStateListener } from "@/components/AuthStateListener";
import { PipelineProvider } from "@/contexts/PipelineContext";
import { TenantProvider } from "@/contexts/TenantContext";
import { RumProvider } from "@/components/RumProvider";
import { OfflineBanner } from "@/components/OfflineBanner";
import { UpdateNotification } from "@/components/UpdateNotification";
import { isConnectionError } from "@/components/ConnectionRetry";
import Auth from "@/pages/Auth";
import ResetPassword from "@/pages/ResetPassword";
import NotFound from "@/pages/NotFound";
import AcceptInvite from "@/pages/AcceptInvite";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import DataDeletionStatus from "@/pages/DataDeletionStatus";
import { PartnerRoute } from "@/components/PartnerRoute";
import InternalRoute from "@/components/InternalRoute";
import { lazyWithRetry } from "@/lib/lazyWithRetry";

// Lazy load partner pages with retry
const PartnerDashboard = lazyWithRetry(() => import("@/pages/partner/PartnerDashboard"));
const PartnerOrgs = lazyWithRetry(() => import("@/pages/partner/PartnerOrgs"));
const PartnerUsers = lazyWithRetry(() => import("@/pages/partner/PartnerUsers"));
const PartnerTeam = lazyWithRetry(() => import("@/pages/partner/PartnerTeam"));
const PartnerTeamPayments = lazyWithRetry(() => import("@/pages/partner/PartnerTeamPayments"));
const PartnerSettings = lazyWithRetry(() => import("@/pages/partner/PartnerSettings"));
const PartnerAssets = lazyWithRetry(() => import("@/pages/partner/PartnerAssets"));
const PartnerCourses = lazyWithRetry(() => import("@/pages/partner/PartnerCourses"));
const PartnerModules = lazyWithRetry(() => import("@/pages/partner/PartnerModules"));
const PartnerRevenueClients = lazyWithRetry(() => import("@/pages/partner/PartnerRevenueClients"));
const PartnerAdsReports = lazyWithRetry(() => import("@/pages/partner/PartnerAdsReports"));
const AdsOAuthCallback = lazyWithRetry(() => import("@/pages/partner/AdsOAuthCallback"));

// Lazy loading para páginas pesadas com retry
const Leads = lazyWithRetry(() => import("@/pages/Leads"));
const Dashboard = lazyWithRetry(() => import("@/pages/Dashboard"));
const Courses = lazyWithRetry(() => import("@/pages/Courses"));
const Farol = lazyWithRetry(() => import("@/pages/Farol"));
const AdminPortal = lazyWithRetry(() => import("@/pages/AdminPortal"));
const OutreachFlows = lazyWithRetry(() => import("@/pages/OutreachFlows"));
const UserManagement = lazyWithRetry(() => import("@/pages/UserManagement"));
const ImportLeads = lazyWithRetry(() => import("@/pages/admin/ImportLeads"));
const Tasks = lazyWithRetry(() => import("@/pages/Tasks"));
const EmailMarketing = lazyWithRetry(() => import("@/pages/EmailMarketing"));
const Meetings = lazyWithRetry(() => import("@/pages/Meetings"));
const Reminders = lazyWithRetry(() => import("@/pages/Reminders"));

// Páginas unificadas (consolidadas)
const GoalsUnified = lazyWithRetry(() => import("@/pages/GoalsUnified"));
const DiscUnified = lazyWithRetry(() => import("@/pages/DiscUnified"));
const SettingsUnified = lazyWithRetry(() => import("@/pages/SettingsUnified"));

// Internal WhatsApp Pages
const WhatsAppChat = lazyWithRetry(() => import("@/pages/internal/WhatsAppChat"));
const WhatsAppDashboard = lazyWithRetry(() => import("@/pages/internal/WhatsAppDashboard"));
const WhatsAppChannels = lazyWithRetry(() => import("@/pages/internal/WhatsAppChannels"));
const WhatsAppTemplates = lazyWithRetry(() => import("@/pages/internal/WhatsAppTemplates"));

// Instagram Direct Pages (hidden - testing only)
const InstagramDirect = lazyWithRetry(() => import("@/pages/internal/InstagramDirect"));
const InstagramOAuthCallback = lazyWithRetry(() => import("@/pages/internal/InstagramOAuthCallback"));
const InstagramChat = lazyWithRetry(() => import("@/pages/internal/InstagramChat"));

// Automations Page (disponível para planos médio e completo)
const Automations = lazyWithRetry(() => import("@/pages/Automations"));

// OTIMIZAÇÃO PERFORMANCE: Cache mais agressivo + retry inteligente para erros de conexão
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 300000, // 5 minutos - dados ficam frescos mais tempo
      gcTime: 900000, // 15 minutos - mantém cache por mais tempo
      // OTIMIZAÇÃO: Retry inteligente para erros de conexão
      retry: (failureCount, error) => {
        // Para erros de conexão (503, 502, etc), retry até 3 vezes
        if (isConnectionError(error)) {
          return failureCount < 3;
        }
        // Para outros erros, apenas 1 retry
        return failureCount < 1;
      },
      retryDelay: (attemptIndex, error) => {
        // Backoff exponencial para erros de conexão
        if (isConnectionError(error)) {
          return Math.min(1000 * Math.pow(2, attemptIndex), 30000);
        }
        return 1000;
      },
      // REMOVIDO: placeholderData causava dados "fantasma" entre sessões de usuários diferentes
      // placeholderData: (previousData: unknown) => previousData,
      refetchOnWindowFocus: false, // Evita refetch ao focar janela
      refetchOnReconnect: true, // Refetch ao reconectar (útil após offline)
      // OTIMIZAÇÃO: Mostrar dados antigos enquanto revalida (stale-while-revalidate)
      placeholderData: (previousData: unknown) => previousData,
    },
  },
});

const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const App = () => (
  <ErrorBoundary>
    <TenantProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
          <TooltipProvider>
            <PipelineProvider>
              {/* Listener para invalidar cache ao trocar usuário */}
              <AuthStateListener />
              {/* Banner de offline para mostrar quando sem conexão */}
              <OfflineBanner />
              <Toaster />
              <Sonner />
              <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                {/* Notificação de nova versão - DEVE estar dentro do Router para useLocation() funcionar */}
                <UpdateNotification autoRefreshOnRouteChange={true} />
                <RumProvider>
                  <Suspense fallback={<LoadingFallback />}>
            <Routes>
              {/* Public legal pages */}
              <Route path="/politicasdeprivacidade" element={<PrivacyPolicy />} />
              <Route path="/termos" element={<TermsOfService />} />
              <Route path="/data-deletion-status" element={<DataDeletionStatus />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/accept-invite" element={<AcceptInvite />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Navigate to="/dashboard" replace />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/leads"
                element={
                  <ProtectedRoute>
                    <ModuleGuard module="leads">
                      <Leads />
                    </ModuleGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/courses"
                element={
                  <ProtectedRoute>
                    <ModuleGuard module="courses">
                      <Courses />
                    </ModuleGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/farol"
                element={
                  <ProtectedRoute>
                    <ModuleGuard module="farol">
                      <Farol />
                    </ModuleGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tasks"
                element={
                  <ProtectedRoute>
                    <ModuleGuard module="tasks">
                      <Tasks />
                    </ModuleGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <AdminRoute>
                    <AdminPortal />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <AdminRoute>
                    <UserManagement />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/import-leads"
                element={
                  <AdminRoute>
                    <ImportLeads />
                  </AdminRoute>
                }
              />
              <Route
                path="/outreach-flows"
                element={
                  <ProtectedRoute>
                    <ModuleGuard module="outreach_flows">
                      <OutreachFlows />
                    </ModuleGuard>
                  </ProtectedRoute>
                }
              />
              
              {/* Metas - Página Unificada com Tabs */}
              <Route
                path="/metas"
                element={
                  <ProtectedRoute>
                    <ModuleGuard module="goals">
                      <GoalsUnified />
                    </ModuleGuard>
                  </ProtectedRoute>
                }
              />
              {/* Redirect rotas antigas de metas para a nova */}
              <Route path="/goals" element={<Navigate to="/metas" replace />} />
              <Route path="/goals/departments" element={<Navigate to="/metas?tab=setoriais" replace />} />
              <Route path="/goals/vendedores" element={<Navigate to="/metas?tab=vendedores" replace />} />
              
              {/* DISC - Página Unificada com Tabs */}
              <Route
                path="/disc"
                element={
                  <ProtectedRoute>
                    <ModuleGuard module="disc">
                      <DiscUnified />
                    </ModuleGuard>
                  </ProtectedRoute>
                }
              />
              {/* Redirect rota antiga de histórico DISC */}
              <Route path="/disc/history" element={<Navigate to="/disc?tab=historico" replace />} />
              
              {/* Configurações - Página Unificada com Tabs */}
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <SettingsUnified />
                  </ProtectedRoute>
                }
              />
              {/* Redirect rotas antigas para a nova estrutura */}
              <Route path="/settings/organizations" element={<Navigate to="/settings?tab=organizacao" replace />} />
              <Route path="/team" element={<Navigate to="/settings?tab=equipe" replace />} />
              
              {/* WhatsApp routes */}
              <Route
                path="/settings/whatsapp/channels"
                element={
                  <ProtectedRoute>
                    <ModuleGuard module="whatsapp">
                      <RoleGuard allowedRoles={["proprietario", "gerente"]}>
                        <WhatsAppChannels />
                      </RoleGuard>
                    </ModuleGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/whatsapp/chat"
                element={
                  <ProtectedRoute>
                    <ModuleGuard module="whatsapp_chat">
                      <WhatsAppChat />
                    </ModuleGuard>
                  </ProtectedRoute>
                }
              />
              {/* Redirect rota antiga para a nova */}
              <Route path="/settings/whatsapp/chat" element={<Navigate to="/whatsapp/chat" replace />} />
              <Route
                path="/email-marketing"
                element={
                  <ProtectedRoute>
                    <ModuleGuard module="email_marketing">
                      <RoleGuard allowedRoles={["proprietario", "gerente"]}>
                        <EmailMarketing />
                      </RoleGuard>
                    </ModuleGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reunioes"
                element={
                  <ProtectedRoute>
                    <ModuleGuard module="meetings">
                      <Meetings />
                    </ModuleGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lembretes"
                element={
                  <ProtectedRoute>
                    <ModuleGuard module="reminders">
                      <Reminders />
                    </ModuleGuard>
                  </ProtectedRoute>
                }
              />
              {/* Automations (disponível para planos médio e completo) */}
              <Route
                path="/automations"
                element={
                  <ProtectedRoute>
                    <ModuleGuard module="automations" fallbackPath="/dashboard">
                      <Automations />
                    </ModuleGuard>
                  </ProtectedRoute>
                }
              />
              {/* Partner Portal Routes */}
              <Route
                path="/partner"
                element={
                  <PartnerRoute>
                    <PartnerDashboard />
                  </PartnerRoute>
                }
              />
              <Route
                path="/partner/receita-clientes"
                element={
                  <PartnerRoute>
                    <PartnerRevenueClients />
                  </PartnerRoute>
                }
              />
              <Route
                path="/partner/ads"
                element={
                  <PartnerRoute>
                    <PartnerAdsReports />
                  </PartnerRoute>
                }
              />
              <Route
                path="/partner/ads/callback"
                element={<AdsOAuthCallback />}
              />
              <Route
                path="/partner/orgs"
                element={
                  <PartnerRoute>
                    <PartnerOrgs />
                  </PartnerRoute>
                }
              />
              {/* Redirect /partner/users to /partner/orgs (users now managed within orgs) */}
              <Route
                path="/partner/users"
                element={<Navigate to="/partner/orgs" replace />}
              />
              <Route
                path="/partner/team"
                element={
                  <PartnerRoute>
                    <PartnerTeam />
                  </PartnerRoute>
                }
              />
              <Route
                path="/partner/team-payments"
                element={
                  <PartnerRoute>
                    <PartnerTeamPayments />
                  </PartnerRoute>
                }
              />
              <Route
                path="/partner/settings"
                element={
                  <PartnerRoute>
                    <PartnerSettings />
                  </PartnerRoute>
                }
              />
              <Route
                path="/partner/assets"
                element={
                  <PartnerRoute>
                    <PartnerAssets />
                  </PartnerRoute>
                }
              />
              <Route
                path="/partner/courses"
                element={
                  <PartnerRoute>
                    <PartnerCourses />
                  </PartnerRoute>
                }
              />
              <Route
                path="/partner/modules"
                element={
                  <PartnerRoute>
                    <PartnerModules />
                  </PartnerRoute>
                }
              />
              {/* WhatsApp Routes (internal) */}
              <Route
                path="/internal/whatsapp/chat"
                element={
                  <InternalRoute>
                    <WhatsAppChat />
                  </InternalRoute>
                }
              />
              <Route
                path="/internal/whatsapp/dashboard"
                element={
                  <InternalRoute>
                    <WhatsAppDashboard />
                  </InternalRoute>
                }
              />
              <Route
                path="/whatsapp/templates"
                element={
                  <ProtectedRoute>
                    <RoleGuard allowedRoles={["proprietario", "gerente"]}>
                      <WhatsAppTemplates />
                    </RoleGuard>
                  </ProtectedRoute>
                }
              />
              {/* Redirect rota antiga (internal) para a nova */}
              <Route
                path="/internal/whatsapp/templates"
                element={<Navigate to="/whatsapp/templates" replace />}
              />
              {/* Instagram Direct Routes (hidden - testing only) */}
              <Route
                path="/direct"
                element={
                  <ProtectedRoute>
                    <RoleGuard allowedRoles={["proprietario", "gerente"]}>
                      <InstagramDirect />
                    </RoleGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/direct/chat"
                element={
                  <ProtectedRoute>
                    <RoleGuard allowedRoles={["proprietario", "gerente"]}>
                      <InstagramChat />
                    </RoleGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/direct/callback"
                element={<InstagramOAuthCallback />}
              />
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
            </RumProvider>
            </BrowserRouter>
            </PipelineProvider>
          </TooltipProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </TenantProvider>
  </ErrorBoundary>
);

export default App;
